import React, { useState } from "react";
import starsicon from "../../images/stars.svg";
import whitearrowicon from "../../images/whitearrow.svg";
import { useSubscription } from "@apollo/client";
import { readToken } from "services/localStorage.service";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { replaceSearchBy } from "store/slices/searcSlice";
import { SEARCH_ASSISTANT_CHATBOT } from "gql/boolean.gql";

export const processCompanyData = (data: any) => {
  const newCompanyList = [];
  if (data) {
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      if (typeof element === "string") {
        newCompanyList.push({ name: element });
      } else if (typeof element === "object") {
        newCompanyList.push(element);
      }
    }
  }

  return newCompanyList;
};

interface SearchQuestionMainProps {
  questionAnswer: any;
  setQuestionAnswer: any;
}

const SearchQuestionMain: React.FC<SearchQuestionMainProps> = ({
  questionAnswer,
  setQuestionAnswer,
}) => {

  const [booleanQuery, setBooleanQuery] = useState("");
  const [searchloading, setSearchLoading] = useState(false);
  const [booleanQueryToExecute, setBooleanQueryToExecute] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();

  const userData = useSelector((state: any) => state.user.user);
  const sessionId = searchParams.get("sessionId");
  const dispatch = useDispatch();

  const {
    data: result,
    loading,
    error,
  } = useSubscription(SEARCH_ASSISTANT_CHATBOT, {
    variables: {
      token: readToken(),
      question: booleanQueryToExecute,
      userEmail: userData?.email,
      sessionId:
        questionAnswer.length > 0 ? questionAnswer[questionAnswer.length - 1]?.sessionId : "",
      hiringCompany: userData?.company?.name || ""
    },
    skip: !booleanQueryToExecute,
    onSubscriptionData: ({ subscriptionData }) => {
      setSearchLoading(false);
      const timestamp = new Date().toISOString();
      setQuestionAnswer([
        ...questionAnswer,
        {
          sender: "user",
          message: subscriptionData?.variables?.question,
          timestamp: timestamp,
          sessionId: subscriptionData?.data?.aiAssistant?.sessionId,
        },
        {
          sender: "bot",
          message: subscriptionData?.data?.aiAssistant?.answer,
          timestamp: timestamp,
          sessionId: subscriptionData?.data?.aiAssistant?.sessionId,
          searchConfig: subscriptionData?.data?.aiAssistant?.searchConfig,
        },
      ]);
      handleSetValuesInRedux(subscriptionData?.data?.aiAssistant?.searchConfig);
      setBooleanQuery("");
      setBooleanQueryToExecute("");
    },
  });

  const handleSetValuesInRedux = (searchConfig: any) => {
    if (booleanQuery && searchConfig) {
      const booleanSearchParsedData = searchConfig;

      dispatch(
        replaceSearchBy({
          jobTitle: booleanSearchParsedData.jobTitles,
          includeLocations: booleanSearchParsedData.locations,
          excludeLocations: booleanSearchParsedData.locationsExclude,
          includeCompanies: processCompanyData(booleanSearchParsedData.companies),
          excludeCompany: booleanSearchParsedData.companiesExclude,
          keywords: booleanSearchParsedData.keywords,
          minExp: booleanSearchParsedData.experienceMin ? booleanSearchParsedData.experienceMin : 1,
          maxExp: booleanSearchParsedData.experienceMax
            ? booleanSearchParsedData.experienceMax
            : 60,
        })
      );
    }
  };

  return (
    <div
      className={`h-calc-vh bg-utility-Grayblue relative 
        custom-scrollbar overflow-y-auto pt-10 transition-all duration-500 ease-in-out w-content-width-active`}
    >
      <div className={`py-32 xl:py-20 w-full`}>
        <div className="text-center ">
          <h1 className="text-5xl font-bold text-fonttext-primary">How can I help?</h1>
          <p className="text-fonttext-primary mt-2.5 text-md">
            Hi my name is{" "}
            <i className="font-bold bg-gradient-to-r  from-primary to-pink-500 inline-block text-transparent bg-clip-text">
              WENDA
            </i>
            , I&apos;m your AI Recruiting Assistant.
          </p>
        </div>
        <div className="xl:px-120px px-20 mt-20 w-full">
          <div className={"flex items-center relative mt-4 w-full"}>
            <img src={starsicon} className="absolute left-4" alt="Stars Icon" />
            <input
              value={booleanQuery}
              className="py-22px ps-52px pe-16 w-full outline-none rounded-xl border border-1 border-main-border focus:border-primary"
              type="text"
              placeholder="I’m looking for ..."
              onChange={(e) => setBooleanQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setBooleanQueryToExecute(booleanQuery);
                }
              }}
            />
            <button
              onClick={() => setBooleanQueryToExecute(booleanQuery)}
              className="bg-primary w-11 h-9 flex justify-center items-center rounded-md absolute right-4 drop-shadow-button-4xl border border-border-switch"
            >
              {!loading ? (
                <img className="w-5 h-5" src={whitearrowicon} alt="Arrow Icon" />
              ) : (
                <svg
                  aria-hidden="true"
                  role="status"
                  className="inline w-4 h-4 animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#fff"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
          {/* <div className="px-2">
                        <List
                            dataSource={suggetions}
                            renderItem={(item) => (
                                <List.Item style={{ cursor: "pointer" }}>
                                    {item}
                                </List.Item>
                            )}
                        />
                    </div> */}
        </div>
      </div>
    </div>
  );
};

export default SearchQuestionMain;
